import React, { useState } from 'react'
import { InputNumber, Button, Form, Modal, Radio, Divider } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons';

import {
  GOGET_SERVICE_PROVIDERS,
  INSTANT_MARKUP_MODES,
  INSTANT_MARKUP_PARTNER_GOGET,
  SERVICE_PROVIDER_DETAILS,
} from '../../constants/serviceProviders'
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const serviceName = (key) =>
  SERVICE_PROVIDER_DETAILS.find(courier => courier.key === key)?.name || key

/**
 * A stored rule as the form wants it. Missing values become null rather than undefined: antd renders an
 * empty InputNumber for both, but only null survives JSON.stringify into the request body — and the
 * settings API merges rather than replaces, so a field left out of the body keeps its old stored value.
 * An admin clearing a floor would otherwise see the input empty and the old floor still in force.
 */
const toFormRule = (rule) => ({
  percentage: rule?.percentage ?? null,
  minMarkup: rule?.minMarkup ?? null,
  maxMarkup: rule?.maxMarkup ?? null,
})

const MarkupSettings = ({ settings, onSave }) => {

  const { permissions } = usePermissions();
  const canEdit = permissions?.includes(PERMISSIONS.UPDATE_SETTING);

  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm();

  const stored = settings?.[INSTANT_MARKUP_PARTNER_GOGET] || {}

  const initialValues = {
    [INSTANT_MARKUP_PARTNER_GOGET]: {
      mode: stored.mode || INSTANT_MARKUP_MODES.uniform,
      uniform: toFormRule(stored.uniform),
      services: GOGET_SERVICE_PROVIDERS.reduce((acc, key) => ({
        ...acc,
        [key]: toFormRule(stored.services?.[key]),
      }), {}),
    },
  }

  // antd 4.16 has no Form.useWatch, so the mode that decides which block renders is mirrored here.
  const [mode, setMode] = useState(initialValues[INSTANT_MARKUP_PARTNER_GOGET].mode)

  const saveHandler = async (newSettings) => {
    setSaving(true)

    Modal.confirm({
      title: 'Confirm to update the instant markup settings?',
      icon: <ExclamationCircleOutlined />,
      content: 'This changes the price merchants are quoted for GoGet instant delivery immediately.',
      onOk() {
        onSave('instantMarkup', newSettings).then(() => {
          setSaving(false)
        })
      },
      onCancel() {
        setSaving(false)
      },
    });
  }

  const rulePath = (key) =>
    key === INSTANT_MARKUP_MODES.uniform
      ? [INSTANT_MARKUP_PARTNER_GOGET, 'uniform']
      : [INSTANT_MARKUP_PARTNER_GOGET, 'services', key]

  const renderRule = (key) => {
    const path = rulePath(key)

    return (
      <React.Fragment key={key}>
        <Form.Item
          extra="Percentage added on top of the GoGet fee. Leave empty or set 0 to sell at cost."
          label="Markup %"
          name={[...path, 'percentage']}
        >
          <InputNumber disabled={!canEdit} min={0} max={100} step={0.5} />
        </Form.Item>

        <Form.Item
          extra="Optional. The markup will never be less than this. Leave empty to apply the percentage alone."
          label="Min Markup (RM)"
          name={[...path, 'minMarkup']}
        >
          <InputNumber disabled={!canEdit} min={0} step={0.1} />
        </Form.Item>

        <Form.Item
          extra="Optional. The markup will never exceed this. Leave empty to apply the percentage alone."
          label="Max Markup (RM)"
          name={[...path, 'maxMarkup']}
          dependencies={[[...path, 'minMarkup']]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const min = getFieldValue([...path, 'minMarkup'])
                if (value === null || value === undefined || min === null || min === undefined) {
                  return Promise.resolve()
                }
                return Number(value) >= Number(min)
                  ? Promise.resolve()
                  : Promise.reject(new Error('Max Markup must be greater than or equal to Min Markup'))
              },
            }),
          ]}
        >
          <InputNumber disabled={!canEdit} min={0} step={0.1} />
        </Form.Item>
      </React.Fragment>
    )
  }

  return (
    <div>
      <Form
        form={form}
        name="markup-settings"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        autoComplete="off"
        initialValues={initialValues}
        requiredMark={false}
        onValuesChange={(changed) => {
          const changedMode = changed?.[INSTANT_MARKUP_PARTNER_GOGET]?.mode
          if (changedMode) setMode(changedMode)
        }}
        onFinish={saveHandler}
      >
        <p className="text-center">
          Markup applied to GoGet instant delivery fees. Merchants are quoted and charged the marked-up
          price; GoGet is still paid the original fee. Delyva services are not marked up.
        </p>

        <Form.Item
          extra="Apply one markup across all GoGet services, or set each service separately."
          label="Markup Mode"
          name={[INSTANT_MARKUP_PARTNER_GOGET, 'mode']}
        >
          <Radio.Group disabled={!canEdit}>
            <Radio.Button value={INSTANT_MARKUP_MODES.uniform}>One rate for all GoGet</Radio.Button>
            <Radio.Button value={INSTANT_MARKUP_MODES.perService}>Rate per service</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {mode === INSTANT_MARKUP_MODES.uniform ? (
          <React.Fragment>
            <Divider>All GoGet Services</Divider>
            {renderRule(INSTANT_MARKUP_MODES.uniform)}
          </React.Fragment>
        ) : (
          GOGET_SERVICE_PROVIDERS.map(key => (
            <React.Fragment key={key}>
              <Divider>{serviceName(key)}</Divider>
              {renderRule(key)}
            </React.Fragment>
          ))
        )}

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button disabled={!canEdit} type="primary" loading={saving} htmlType="submit">Save</Button>
        </Form.Item>
      </Form>
    </div>
  )
}

export default MarkupSettings
